import React, { useEffect } from "react";
import useInterval from "./useInterval";
import { periodTypes } from "../../mls-match-list/utils";
import { formatPenaltyKicks } from "../../mls-match-summary/utils";

export const actionTypes = {
  period: 'period',
  minuteDisplay: 'minuteDisplay',
  homeScore: 'homeScore',
  awayScore: 'awayScore',
  homeShootoutScore: 'homeShootoutScore',
  awayShootoutScore: 'awayShootoutScore',
  resetIsRecentGoal: 'resetIsRecentGoal',
  isGoalTimerRunning: 'isGoalTimerRunning',
  count: 'count',
  goals: 'goals',
  summary: 'summary',
  penaltyKicks: 'penaltyKicks',
  resultType: "resultType",
  matchType: "matchType",
  winnerStatus: "winnerStatus",
  homeExtraScore: "homeExtraScore",
  awayExtraScore: "awayExtraScore",
};

export function matchReducer(state, action) {
  switch (action.type) {
    case actionTypes.period: {
      return { ...state, period: action.payload };
    }
    case actionTypes.homeExtraScore: {
      return { ...state, homeExtraScore: action.payload };
    }
    case actionTypes.awayExtraScore: {
      return { ...state, awayExtraScore: action.payload };
    }
    case actionTypes.winnerStatus: {
      return { ...state, winnerStatus: action.payload };
    }
    case actionTypes.minuteDisplay: {
      if (state.period === periodTypes.HalfTime) {
        return { ...state, minuteDisplay: "HT" };
      }
      return { ...state, minuteDisplay: action.payload };
    }
    case actionTypes.summary: {
      return { ...state, summary: action.payload };
    }
    case actionTypes.resultType: {
      return { ...state, resultType: action.payload };
    }
    case actionTypes.penaltyKicks: {
      if (state.penaltyKicks.length === action.payload.length) {
        return { ...state };
      }
      const { home, away } = state || {};
      const penaltyKicks = formatPenaltyKicks({ penaltyKicks: action.payload, home, away });
      return { ...state, penaltyKicks };
    }
    case actionTypes.homeShootoutScore: {
      return { ...state, homeShootoutScore: action.payload };
    }
    case actionTypes.awayShootoutScore: {
      return { ...state, awayShootoutScore: action.payload };
    }
    case actionTypes.awayScore: {
      return { ...state, awayScore: action.payload };
    }
    case actionTypes.homeScore: {
      return { ...state, homeScore: action.payload };
    }

    case actionTypes.goals: {
      if (action.payload?.length > 0) {
        const now = Date.now();
        const lastGoal = action.payload[action.payload.length - 1];
        const lastGoalTimestamp = lastGoal?.timestamp;
        const period = lastGoal?.period;

        const isRecentGoal = (now - lastGoalTimestamp) < 100000;

        if (isRecentGoal && periodTypes.ShootOut !== period) {
          return { ...state, goals: action.payload, isRecentGoal };
        }
        else {
          return { ...state, goals: action.payload };
        }
      }

      return { ...state };
    }

    case actionTypes.isGoalTimerRunning: {
      return { ...state, isGoalTimerRunning: action.payload };
    }

    case actionTypes.resetIsRecentGoal: {
      return { ...state, isRecentGoal: false, isGoalTimerRunning: false, count: 0 };
    }

    case actionTypes.count: {
      return { ...state, count: action.payload };
    }

    default: {
      throw new Error(`Unsupported type: ${action.type}`);
    }
  }
}

export function useMatchInfo({ initialState, reducer = matchReducer } = {}) {
  const { current: currentState } = React.useRef(initialState);
  const [state, dispatch] = React.useReducer(reducer, currentState);

  const updateMatchInfo = ({ type, payload }) => dispatch({ type, payload });

  const { isRecentGoal, isGoalTimerRunning, count } = state || {};

  useEffect(() => {
    if (isRecentGoal) {
      updateMatchInfo({ type: actionTypes.isGoalTimerRunning, payload: true });
    }
  }, [isRecentGoal]);

  useInterval(() => {
    if (count === 15) {
      updateMatchInfo({ type: actionTypes.resetIsRecentGoal, payload: false });
    }
    else {
      updateMatchInfo({ type: actionTypes.count, payload: count + 1 });
    }
  }, isGoalTimerRunning ? 1000 : null);

  return {
    state,
    updateMatchInfo
  };
}
